import type { Branch } from '@/features/auth/types';

export type IdentityType = 'mykad' | 'passport';
export type AssistanceNeed =
  'wheelchair_user' | 'hearing_impaired' | 'interpreter';
export type ConsentType =
  | 'clinical_care'
  | 'panel_release'
  | 'notifications'
  | 'marketing'
  | 'ai_assist';
export type EkycStatus = 'unverified' | 'verified';
export type MemberPriceStatus = 'none' | 'pending' | 'active';
export type ContactKind = 'emergency_contact' | 'next_of_kin' | 'safe_contact';
export type PatientStatus = 'active' | 'inactive' | 'merged';

export interface PatientIdentity {
  type: IdentityType;
  /** Display form: MyKad as 880412-04-5531. */
  value: string;
  issuingCountry: string;
}

/** Row in GET /patients. */
export interface PatientSummary {
  id: string;
  fullName: string;
  nationality: string;
  phone: string | null;
  status: PatientStatus;
  registeredBranch: Branch;
  identity: PatientIdentity | null;
  coverage: {
    id: string;
    payer: Payer;
    memberNo: string;
  } | null;
  memberPriceStatus: MemberPriceStatus;
  ekycStatus: EkycStatus;
  lastVisitAt: string | null;
  createdAt: string;
}

export interface Payer {
  id: string;
  code: string;
  name: string;
  kind?: 'panel' | 'insurer';
}

export type CoverageSource = 'counter' | 'app';
export type CoverageCheck = 'checked' | 'pending_check';

export interface Coverage {
  id: string;
  payer: Payer;
  memberNo: string;
  yearlyLimitSen: number | null;
  usedSen: number;
  coPayment: string | null;
  notCovered: string | null;
  validUntil: string | null;
  /** RL-896: entered at the counter, or by the patient in the app. */
  source: CoverageSource;
  /**
   * pending_check = entered in the app and not yet checked at the counter
   * (not used as a payer until then). Counter entries are always checked.
   */
  check: CoverageCheck;
  /** When the counter checked an app-entered membership (null for counter entries). */
  checkedAt: string | null;
}

/** RL-1171: the merged record an allergy is recorded on. */
export interface AllergyFromRecord {
  id: string;
  fullName: string;
}

/** How an app account was linked to the record (StaffAppAccountDto.method). */
export type AppLinkMethod =
  | 'ekyc'
  | 'mydigital_id'
  | 'clinic_check'
  | 'self_registration'
  | 'phone_second_check'
  | 'test_account';

/**
 * Security review S1 (API PR #58): the active app link on the record (or a
 * record merged into it), as in GET /patients/:id `appAccount`.
 */
export interface AppAccount {
  ownershipId: string;
  patientAccountId: string;
  /** The phone the app account verified, masked (e.g. +60 12-*** **21). */
  phone: string | null;
  method: AppLinkMethod;
  linkedAt: string;
  /**
   * True while a self-registered record is not identity-verified: the app
   * gets no clinical data or pushes until counter eKYC confirms the account.
   */
  identityPending: boolean;
}

/** RL-1325: one app account returned by GET /patients/:id/app-sign-in. */
export interface PatientAppSignInAccount {
  accountId: string;
  /** The compulsory verified phone, masked by the API. */
  phoneMasked: string | null;
  /** The confirmed sign-in email, masked by the API. */
  emailMasked: string | null;
  emailConfirmedAt: string | null;
  password: 'not_set' | 'set' | 'disabled_by_clinic';
  passwordSetAt: string | null;
  passwordDisabledAt: string | null;
  passwordDisabledReason: string | null;
  /** Set while password sign-in is temporarily locked after failed attempts. */
  lockedUntil: string | null;
}

/** RL-1325: staff view of app sign-in methods linked to a patient record. */
export interface PatientAppSignInMethods {
  accounts: PatientAppSignInAccount[];
}

export const APP_SIGN_IN_REASON_LIMITS = { min: 5, max: 300 } as const;

/**
 * The app account the counter is asked about: from `appAccount` on the record
 * or the `appAccount` extension of 422 APP_ACCOUNT_CONFIRMATION_REQUIRED
 * (empty method/linkedAt when the problem did not carry them).
 */
export interface AppAccountRef {
  phone: string | null;
  method: AppLinkMethod | string;
  linkedAt: string;
}

/**
 * RL-1185: POST /patients/:id/app-account/unlink `reason` (trimmed, required;
 * UnlinkAppAccountDto in the API).
 */
export const UNLINK_REASON_LIMITS = { min: 5, max: 300 } as const;

/** Counter eKYC answer to "Is this app account the patient's?". */
export type AppAccountAnswer = 'confirmed' | 'not_theirs';

/**
 * RL-951: `reported` = the patient added it in the app and no doctor has
 * confirmed it yet (shown "Patient-reported, not confirmed"; it is in the
 * prescribing checks too). Staff views never list rejected or withdrawn ones.
 */
export type AllergyStatus = 'confirmed' | 'reported';
export type AllergySeverity = 'mild' | 'moderate' | 'severe';
/** Prescribing allergy groups (AddAllergyDto / ConfirmAllergyDto). */
export type AllergyGroup =
  'penicillin' | 'nsaid' | 'sulfonamide' | 'benzodiazepine';

export const ALLERGY_GROUP_LABELS: Record<AllergyGroup, string> = {
  penicillin: 'Penicillin',
  nsaid: 'NSAIDs',
  sulfonamide: 'Sulfonamides',
  benzodiazepine: 'Benzodiazepines',
};
export const ALLERGY_SEVERITY_LABELS: Record<AllergySeverity, string> = {
  mild: 'Mild',
  moderate: 'Moderate',
  severe: 'Severe',
};

export interface Allergy {
  id: string;
  substance: string;
  allergyGroup: string | null;
  reaction: string | null;
  severity?: AllergySeverity | null;
  source?: 'reported' | 'adverse_reaction';
  /** RL-951: confirmed, or reported by the patient in the app (not confirmed). */
  status: AllergyStatus;
  /** RL-951 (patient record only): added by the patient in the app. */
  reportedInApp?: boolean;
  /** Patient record only. */
  recordedAt?: string;
  /** RL-951 (patient record only): when a doctor confirmed it; null while reported. */
  confirmedAt?: string | null;
  /**
   * RL-1171: recorded on another record of the patient's merged-record family
   * (show it like the others, with a "From merged record <name>" hint).
   */
  fromMergedRecord: boolean;
  /** RL-1171: that record when `fromMergedRecord`; null when on this record. */
  fromRecord: AllergyFromRecord | null;
}

/** RL-951 blood type values (UpdatePatientDto / StaffBloodTypeDto). */
export const BLOOD_TYPES = [
  'A+',
  'A-',
  'B+',
  'B-',
  'AB+',
  'AB-',
  'O+',
  'O-',
  'unknown',
] as const;
export type BloodTypeValue = (typeof BLOOD_TYPES)[number];

/**
 * RL-951: the patient's blood type. `patient_reported` = set by the patient in
 * the app, not confirmed; staff confirm or correct it with PATCH
 * /patients/:id { bloodType } (saved as `staff_confirmed`).
 */
export interface PatientBloodType {
  value: BloodTypeValue;
  source: 'patient_reported' | 'staff_confirmed';
  updatedAt: string | null;
}

/**
 * RL-951: the profile photo the patient set in the app (not an identity
 * check). The image is GET `path` (patients:read, audited).
 */
export interface PatientPhoto {
  id: string;
  contentType: 'image/jpeg' | 'image/png';
  sizeBytes: number;
  updatedAt: string;
  /** e.g. /api/v1/patients/:id/photo; the admin calls /patients/:id/photo. */
  path: string;
}

export interface PatientContact {
  id: string;
  kind: ContactKind;
  name: string;
  relationship: string | null;
  phone: string;
}

/** GET /patients/:id */
export interface PatientDetail extends PatientSummary {
  dateOfBirth: string | null;
  sex: 'male' | 'female' | null;
  email: string | null;
  address: {
    line1: string | null;
    line2: string | null;
    postcode: string | null;
    city: string | null;
    state: string | null;
  };
  assistanceNeeds: AssistanceNeed[];
  assistanceNote: string | null;
  identities: (PatientIdentity & {
    id: string;
    isPrimary: boolean;
    endedAt: string | null;
  })[];
  contacts: PatientContact[];
  consents: Record<
    ConsentType,
    { granted: boolean; recordedAt: string | null }
  >;
  mergedInto: { id: string; fullName: string } | null;
  mergedFrom: { id: string; fullName: string; mergedAt: string | null }[];
  ekyc: {
    status: EkycStatus;
    verifiedAt: string | null;
    method: 'app' | 'counter' | null;
  };
  safeContactOnly: boolean;
  /** Active app link; null = none. */
  appAccount: AppAccount | null;
  /** RL-955: when the patient deleted a linked app account; null = never. */
  appAccountDeletedAt: string | null;
  /** RL-951: null = not recorded. Optional: older API builds omit it. */
  bloodType?: PatientBloodType | null;
  /** RL-951: the photo the patient set in the app; null = none. */
  photo?: PatientPhoto | null;
  /** Stand-in for the app's arrival pass (staging; no patient app yet). */
  arrivalPassToken: string | null;
  coverages: Coverage[];
  allergies: Allergy[];
  inQueue: {
    ticketId: string;
    number: number;
    status: 'waiting' | 'in_room';
    room: string | null;
    branch: { id: string; name: string };
    clinicDate: string;
  } | null;
  bills: {
    id: string;
    number: string;
    status: 'open' | 'closed' | 'void';
    totalSen: number;
    paidSen: number;
    openedAt: string;
    payer: { id: string; name: string } | null;
  }[];
  version: number;
  updatedAt: string;
  createdBy: { id: string; fullName: string } | null;
  updatedBy: { id: string; fullName: string } | null;
}

export interface CreatePatientInput {
  identity: { type: IdentityType; value: string };
  nationality?: string;
  fullName: string;
  phone?: string;
  assistanceNeeds?: AssistanceNeed[];
  consents?: Partial<Record<ConsentType, boolean>>;
}

export interface UpdatePatientInput {
  version: number;
  phone?: string;
  email?: string;
  addressLine1?: string;
  addressLine2?: string;
  postcode?: string;
  city?: string;
  state?: string;
  assistanceNeeds?: AssistanceNeed[];
  consents?: Partial<Record<ConsentType, boolean>>;
  safeContactOnly?: boolean;
  /** RL-951: saved as staff_confirmed (null clears it). */
  bloodType?: BloodTypeValue | null;
  contacts?: {
    add?: Omit<PatientContact, 'id'>[];
    removeIds?: string[];
  };
}

export interface DuplicatePair {
  a: PatientSummary & { registeredAt: string };
  b: PatientSummary & { registeredAt: string };
  reason:
    'same_document' | 'same_name_and_birth_date' | 'same_phone_and_first_name';
}

export type DisclosureRequester =
  'patient' | 'family_member' | 'employer' | 'insurer_panel' | 'police_court';
export type DisclosureTopic =
  'appointment_time' | 'bill_amount' | 'test_results' | 'documents';
export type DisclosureCheck =
  'app_approved' | 'ic_dob_question' | 'written_consent' | 'not_verified';

export const REQUESTER_LABELS: Record<DisclosureRequester, string> = {
  patient: 'The patient',
  family_member: 'Family member',
  employer: 'Employer',
  insurer_panel: 'Insurer or panel',
  police_court: 'Police or court',
};
export const TOPIC_LABELS: Record<DisclosureTopic, string> = {
  appointment_time: 'Appointment time',
  bill_amount: 'Bill amount',
  test_results: 'Test results',
  documents: 'MC or documents',
};
export const CHECK_LABELS: Record<DisclosureCheck, string> = {
  app_approved: 'Patient approved in the app',
  ic_dob_question: 'IC, date of birth and security question',
  written_consent: 'Written consent on file',
  not_verified: 'Not verified',
};

export interface DisclosureEntry {
  id: string;
  patient: { id: string; fullName: string };
  requester: DisclosureRequester;
  topic: DisclosureTopic;
  identityCheck: DisclosureCheck;
  requesterName: string | null;
  outcome: 'released' | 'refused' | 'referred';
  recordedAt: string;
  recordedBy: { id: string; fullName: string };
}

export interface DisclosureResult {
  id: string;
  outcome: 'released' | 'refused' | 'referred';
  mayRelease: DisclosureTopic[];
  message: string;
}

export interface DuplicateCheckResult {
  identityMatch: PatientSummary | null;
  possibleMatches: PatientSummary[];
}

export const CONSENT_LABELS: Record<ConsentType, string> = {
  clinical_care: 'Clinical',
  panel_release: 'Panel release',
  notifications: 'Notifications',
  marketing: 'Marketing',
  ai_assist: 'AI suggestions',
};
/** The four consent chips on the counter (AI consent is recorded by the doctor). */
export const COUNTER_CONSENTS: ConsentType[] = [
  'clinical_care',
  'panel_release',
  'notifications',
  'marketing',
];
export const MEMBER_PRICE_LABELS: Record<MemberPriceStatus, string> = {
  none: '-',
  pending: 'Pending',
  active: 'Active',
};

export const ASSISTANCE_LABELS: Record<AssistanceNeed, string> = {
  wheelchair_user: 'Wheelchair user',
  hearing_impaired: 'Hearing impaired: show the number on screen',
  interpreter: 'Interpreter',
};

export const CONTACT_LABELS: Record<ContactKind, string> = {
  emergency_contact: 'Emergency contact',
  next_of_kin: 'Next of kin',
  safe_contact: 'Safe contact',
};
